<template>
	<div class="container pt-6 pb-10">
		<div class="flex justify-between items-center py-2 mb-6">
			<h1 class="page-title">Rapoarte productie</h1>
		</div>

		<!-- Filter Bar -->
		<v-toolbar flat color="light-grey" class="rounded mb-4" height="auto">
			<div class="grid grid-cols-3 gap-3 py-3 w-full items-center">
				<!-- Date range picker -->
				<v-menu v-model="datepickerMenu" :close-on-content-click="false" :nudge-left="40"
					transition="scale-transition" offset-y min-width="auto">
					<template v-slot:activator="{ on, attrs }">
						<v-text-field v-model="dateRangeText" label="Perioada" solo prepend-inner-icon="mdi-calendar"
							hide-details readonly v-bind="attrs" v-on="on"></v-text-field>
					</template>
					<v-date-picker v-model="newDateRange" no-title range>
						<v-spacer></v-spacer>
						<v-btn text color="teal" @click="datepickerMenu = false">Inchide</v-btn>
						<v-btn text color="teal" @click="saveDates">OK</v-btn>
					</v-date-picker>
				</v-menu>

				<!-- Agent selector -->
				<v-select ref="selectAgent" v-model="selectedAgentId" :items="agentItems" item-text="username"
					item-value="id_user" label="Toti agentii" solo hide-details clearable :loading="loadingFenceStatsUsers"
					@click:clear="$refs.selectAgent.blur()" prepend-inner-icon="mdi-account">
				</v-select>

			</div>
		</v-toolbar>

		<!-- Objectives cards — single-agent only, above KPIs -->
		<AgentObjectivesProgress v-if="selectedAgent && selectedAgent.agent_market === 'ro'"
			:title="selectedAgent ? `Obiective - ${selectedAgent.username}` : 'Obiective'" :user-id="Number(selectedAgent.id_user)"
			:user-zone="selectedAgent.zona" :user-role="selectedAgent.rol" :date-range="dateRange" />

		<!-- KPI Summary + Funnel -->
		<FunnelKPIs :title="selectedAgent ? `Rezumat - ${selectedAgent.username}` : 'Rezumat - toti agentii'"
			:configs="activeFunnel.configs" :offers="activeFunnel.offers" :contracts="activeFunnel.contracts"
			:loading="loadingFunnelAgents" />

		<!-- All-agents breakdown table -->
		<AgentBreakdownTable v-if="!selectedAgentId"
			:title="selectedAgent ? `Obiective - ${selectedAgent.username}` : 'Obiective'" :agents="funnelAgentsWithRates"
			:loading="loadingFunnelAgents" />

		<!-- Month-by-month table — single-agent only -->
		<AgentMonthlyTable v-if="selectedAgent"
			:title="selectedAgent ? `Evoluție lunară - ${selectedAgent.username}` : 'Evoluție lunară'"
			:funnel-monthly="funnelMonthly" :loading="loadingFunnelMonthly" />

		<!-- Zone 3: County detail — hidden in single-agent view -->
		<div v-if="!selectedAgentId" class="mt-2">
			<ConfiguratorLeadsProforme :from-date="dateRange[0]" :to-date="dateRange[1]" />
		</div>
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import FunnelKPIs from './Productie/FunnelKPIs.vue';
import AgentBreakdownTable from './Productie/AgentBreakdownTable.vue';
import AgentObjectivesProgress from './Productie/AgentObjectivesProgress.vue';
import AgentMonthlyTable from './Productie/AgentMonthlyTable.vue';
import ConfiguratorLeadsProforme from './Productie/LeadsProforme.vue';

export default {
	name: 'RapoarteProductie',
	components: {
		FunnelKPIs,
		AgentBreakdownTable,
		AgentObjectivesProgress,
		AgentMonthlyTable,
		ConfiguratorLeadsProforme,
	},
	data() {
		return {
			datepickerMenu: false,
			dateRange: [
				new Date(new Date().getFullYear(), new Date().getMonth(), 2).toISOString().split('T')[0],
				new Date().toISOString().split('T')[0],
			],
			newDateRange: [],
			selectedAgentId: null,
		};
	},
	computed: {
		...mapState('userss', ['fenceStatsUsers', 'loadingFenceStatsUsers']),
		...mapState('reports', {
			loadingFunnelAgents: state => state.loadingFunnelAgents,
			funnelAgents: state => state.funnelAgents,
			loadingFunnelMonthly: state => state.loadingFunnelMonthly,
			funnelMonthly: state => state.funnelMonthly,
		}),
		...mapGetters('reports', ['funnelTotals', 'funnelAgentsWithRates']),
		agentItems() {
			return this.fenceStatsUsers;
		},
		selectedAgent() {
			return this.selectedAgentId
				? this.fenceStatsUsers.find(u => u.id_user === this.selectedAgentId) || null
				: null;
		},
		activeFunnel() {
			if (this.selectedAgentId) {
				const agent = this.funnelAgentsWithRates.find(a => Number(a.agent_id) === Number(this.selectedAgentId));
				if (agent) return agent;
			}
			return this.funnelTotals;
		},
		dateRangeText() {
			const d1 = this.$options.filters.formatDate(this.dateRange[0]);
			const d2 = this.$options.filters.formatDate(this.dateRange[1]);
			return `${d1 || ''} → ${d2 || ''}`;
		},
		monthlyWindowDates() {
			const now = new Date();
			const fromDate = new Date(now.getFullYear(), now.getMonth() - 5, 2).toISOString().split('T')[0];
			const toDate = now.toISOString().split('T')[0];
			return { fromDate, toDate };
		},
	},
	watch: {
		dateRange(newVal) {
			this.fetchFunnelData(newVal[0], newVal[1]);
		},
		selectedAgentId(newVal) {
			if (newVal) {
				const { fromDate, toDate } = this.monthlyWindowDates;
				this.fetchFunnelMonthly({ userId: newVal, fromDate, toDate });
			}
		},
	},
	methods: {
		...mapActions('reports', ['fetchFunnelAgents', 'fetchFunnelMonthly']),
		...mapActions('userss', ['fetchFenceAgents']),
		saveDates() {
			if (this.newDateRange.length < 2) return;
			const [d1, d2] = this.newDateRange;
			this.dateRange = new Date(d1) <= new Date(d2) ? [d1, d2] : [d2, d1];
			this.datepickerMenu = false;
		},
		fetchFunnelData(fromDate, toDate) {
			this.fetchFunnelAgents({ fromDate, toDate });
			if (this.selectedAgentId) {
				const { fromDate: mFrom, toDate: mTo } = this.monthlyWindowDates;
				this.fetchFunnelMonthly({ userId: this.selectedAgentId, fromDate: mFrom, toDate: mTo });
			}
		},
	},
	mounted() {
		this.newDateRange = [...this.dateRange];
		this.fetchFenceAgents();
		this.fetchFunnelData(this.dateRange[0], this.dateRange[1]);
	},
};
</script>

<style scoped>
.v-toolbar {
	height: auto !important;
}
</style>
